/**
 * SettingsTab — the schema-driven general-settings editor.
 *
 * Renders the Operations (tier 1) and Advanced (tier 2) tabs from the SAME
 * server schema (GET /api/admin/settings): group the fields, render each by its
 * `kind`, track a per-key draft, and PUT only the changed keys. Because the
 * server owns the schema, a new editable tenant_config key shows up here with
 * zero changes to this file (CLAUDE.md §7).
 *
 * Tier 2 groups render inside collapsed <details> with the same validation, so
 * the deeper tuning knobs are available but out of the way (and a bad value is
 * rejected server-side with a clear message rather than silently breaking a run).
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { SettingField } from "@/types";
import { Button, Card, SelectField } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { SavedNote, NumberField } from "./shared";

const SETTINGS_KEY = ["admin", "settings"] as const;

/** Structural equality for the value types the schema carries. */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === b[i]);
  }
  if (a && b && typeof a === "object" && typeof b === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
}

export function SettingsTab({ tier }: { tier: 1 | 2 }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: ({ signal }) => api.adminGetSettings(signal),
  });

  // Draft values keyed by setting key; raw text mirrors for list + JSON editing.
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [listText, setListText] = useState<Record<string, string>>({});
  const [jsonText, setJsonText] = useState<Record<string, string>>({});
  const [jsonError, setJsonError] = useState<Record<string, string>>({});
  const [justSaved, setJustSaved] = useState(false);

  const allFields = query.data?.fields;
  const fields = useMemo(
    () => (allFields ?? []).filter((f) => f.tier === tier),
    [allFields, tier],
  );

  // Seed the draft + text mirrors whenever the server data (re)loads.
  useEffect(() => {
    if (!allFields) return;
    const d: Record<string, unknown> = {};
    const lt: Record<string, string> = {};
    const jt: Record<string, string> = {};
    for (const f of allFields) {
      d[f.key] = f.value;
      if (f.kind === "string_list") lt[f.key] = Array.isArray(f.value) ? f.value.join(", ") : "";
      if (f.kind === "json") jt[f.key] = JSON.stringify(f.value ?? {}, null, 2);
    }
    setDraft(d);
    setListText(lt);
    setJsonText(jt);
    setJsonError({});
    setJustSaved(false);
  }, [allFields]);

  const save = useMutation({
    mutationFn: (updates: Record<string, unknown>) => api.adminSaveSettings(updates),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      setJustSaved(true);
    },
  });

  if (query.isLoading) return <LoadingState label="Loading settings…" />;
  if (query.isError || !allFields) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const serverByKey = new Map(allFields.map((f) => [f.key, f.value]));
  const changedKeys = fields
    .map((f) => f.key)
    .filter((k) => !valuesEqual(draft[k], serverByKey.get(k)));
  const hasJsonError = Object.values(jsonError).some(Boolean);
  const dirty = changedKeys.length > 0;
  const saving = save.isPending;

  function touch() {
    if (justSaved) setJustSaved(false);
  }

  function setValue(key: string, value: unknown) {
    touch();
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function setListValue(field: SettingField, raw: string) {
    touch();
    setListText((m) => ({ ...m, [field.key]: raw }));
    const parsed = raw.split(",").map((s) => s.trim()).filter(Boolean);
    setDraft((d) => ({ ...d, [field.key]: parsed }));
  }

  function setJsonValue(field: SettingField, raw: string) {
    touch();
    setJsonText((m) => ({ ...m, [field.key]: raw }));
    try {
      const parsed: unknown = raw.trim() === "" ? {} : JSON.parse(raw);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setJsonError((m) => ({ ...m, [field.key]: "Must be a JSON object, e.g. {}" }));
        return;
      }
      setJsonError((m) => ({ ...m, [field.key]: "" }));
      setDraft((d) => ({ ...d, [field.key]: parsed }));
    } catch {
      setJsonError((m) => ({ ...m, [field.key]: "Invalid JSON" }));
    }
  }

  function saveAll() {
    if (hasJsonError) return;
    setJustSaved(false);
    const updates: Record<string, unknown> = {};
    for (const k of changedKeys) updates[k] = draft[k];
    save.mutate(updates);
  }

  // Stable group order = first appearance in the (server-ordered) field list.
  const groups: string[] = [];
  for (const f of fields) if (!groups.includes(f.group)) groups.push(f.group);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">
            {tier === 1 ? "Operations" : "Advanced"}
          </h2>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {tier === 1
              ? "Day-to-day operating settings: identity, ingestion, live-coaching scope, data retention, and the transcript-source cutover."
              : "Deeper tuning — STT, AI/classifier parameters, quality gates, polling, alerts, and product rules. Values are validated on save; out-of-range edits are rejected with a reason."}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <SavedNote show={justSaved && !dirty} />
          <Button onClick={saveAll} disabled={saving || !dirty || hasJsonError}>
            {saving ? "Saving…" : dirty ? `Save ${changedKeys.length} change${changedKeys.length === 1 ? "" : "s"}` : "Save changes"}
          </Button>
        </div>
      </div>

      {save.isError && (
        <p className="text-sm text-red-600">
          {(save.error as { message?: string } | null)?.message ??
            "Couldn't save your changes. A value may be out of range — please check and try again."}
        </p>
      )}

      {groups.map((group) => {
        const groupFields = fields.filter((f) => f.group === group);
        const body = (
          <div className="flex flex-col gap-4">
            {groupFields.map((f) => (
              <FieldRow
                key={f.key}
                field={f}
                value={draft[f.key]}
                listText={listText[f.key] ?? ""}
                jsonText={jsonText[f.key] ?? ""}
                jsonError={jsonError[f.key] ?? ""}
                onValue={(v) => setValue(f.key, v)}
                onList={(raw) => setListValue(f, raw)}
                onJson={(raw) => setJsonValue(f, raw)}
              />
            ))}
          </div>
        );
        // Tier 2 groups collapse; tier 1 groups are plain cards.
        if (tier === 2) {
          return (
            <details key={group} className="rounded-lg border bg-card">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold">
                {group}
              </summary>
              <div className="border-t p-4">{body}</div>
            </details>
          );
        }
        return (
          <Card key={group} className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold">{group}</h3>
            {body}
          </Card>
        );
      })}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition " +
        (checked ? "bg-primary" : "bg-muted")
      }
    >
      <span
        className={
          "inline-block h-4 w-4 transform rounded-full bg-background transition " +
          (checked ? "translate-x-6" : "translate-x-1")
        }
      />
    </button>
  );
}

function FieldRow({
  field,
  value,
  listText,
  jsonText,
  jsonError,
  onValue,
  onList,
  onJson,
}: {
  field: SettingField;
  value: unknown;
  listText: string;
  jsonText: string;
  jsonError: string;
  onValue: (v: unknown) => void;
  onList: (raw: string) => void;
  onJson: (raw: string) => void;
}) {
  const { kind } = field;

  // Boolean: label + help on the left, toggle on the right.
  if (kind === "boolean") {
    return (
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium">{field.label}</p>
          {field.help && <p className="text-sm text-muted-foreground">{field.help}</p>}
        </div>
        <Toggle checked={value === true} onChange={onValue} />
      </div>
    );
  }

  if (kind === "int" || kind === "number") {
    const clamp = (n: number) => {
      let v = kind === "int" ? Math.round(n) : n;
      if (field.min !== undefined) v = Math.max(field.min, v);
      if (field.max !== undefined) v = Math.min(field.max, v);
      return v;
    };
    return (
      <div className="flex flex-col gap-1">
        <NumberField
          label={field.label}
          value={typeof value === "number" ? value : 0}
          min={field.min}
          onChange={(v) => onValue(clamp(v))}
          className="max-w-[200px]"
        />
        {field.help && <p className="text-sm text-muted-foreground">{field.help}</p>}
      </div>
    );
  }

  if (kind === "enum") {
    return (
      <div className="flex flex-col gap-1">
        <SelectField
          label={field.label}
          options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onValue(e.target.value)}
          className="max-w-md"
        />
        {field.help && <p className="text-sm text-muted-foreground">{field.help}</p>}
      </div>
    );
  }

  if (kind === "string_list") {
    return (
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{field.label}</span>
        <input
          value={listText}
          onChange={(e) => onList(e.target.value)}
          placeholder={field.placeholder ?? "comma, separated, values"}
          className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
        {field.help && <span className="text-muted-foreground">{field.help}</span>}
      </label>
    );
  }

  if (kind === "json") {
    return (
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{field.label}</span>
        <textarea
          value={jsonText}
          onChange={(e) => onJson(e.target.value)}
          rows={6}
          spellCheck={false}
          className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:ring-2 focus:ring-ring"
        />
        {jsonError ? (
          <span className="text-red-600">{jsonError}</span>
        ) : (
          field.help && <span className="text-muted-foreground">{field.help}</span>
        )}
      </label>
    );
  }

  // string / text
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{field.label}</span>
      {kind === "text" ? (
        <textarea
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onValue(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder={field.placeholder}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      ) : (
        <input
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onValue(e.target.value)}
          placeholder={field.placeholder}
          className="max-w-md rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      )}
      {field.help && <span className="text-muted-foreground">{field.help}</span>}
    </label>
  );
}
